import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import {
  appTextColor,
  white,
  defaultBoxShadow,
  warningRedColor,
  focusedInputBorderColor,
  unfocusedInputBorderColor,
  appMainBackground
} from '../../../styles/colors';
import { authSelectors } from '@rosetta/react-auth';
import { EMAIL_STATUS, launchpadActions, launchpadSelectors } from '../reducer/launchpad-reducer';
import { lionSelectors, LionTranslate } from '@rosetta/react-lion/dist/index';
import { LaunchpadButton } from '../../../styles/LaunchpadButton';
import checkIcon from '../../../assets/images/success-check.svg';
import mobileIcon from '../../../assets/images/img-mobileapp.png';
import { appSelectors } from '../../app/reducer/app-reducer';
import { validateUserInfo } from '../../app/util/user-helper';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { isMobile as isMobileDetected } from 'react-device-detect';

const EmailLauncherBox = styled.div((props) => {
  let style = {
    width: !props.isMobile ? '530px' : '100%',
    marginTop: 0,
    padding: !props.isMobile ? '18px 0 30px 119px' : '18px 0 30px 125px',
    backgroundColor: !props.isMobile ? white : appMainBackground,
    borderRadius: '4px',
    backgroundImage: 'url(' + mobileIcon + ')',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: !props.isMobile ? '8px 5px' : '18px 5px',
    height: !props.isMobile ? '130px' : '168px'
  };

  if (!props.isMobile) {
    style.boxShadow = defaultBoxShadow;
    style.marginBottom = '10px';
  } else {
    style.marginTop = '12px';
  }
  return style;
});

const EmailLauncherHeading = styled.div(() => {
  let style = {
    fontSize: '17px',
    fontWeight: '500',
    letterSpacing: '1px',
    lineHeight: '22px'
  };
  return style;
});

const LaunchContainer = styled.div((props) => {
  let style = {
    marginTop: '16px',
    float: !props.isMobile ? 'left' : 'none',
    width: !props.isMobile ? '62%' : '85%'
  };
  return style;
});

const InputWithoutIcon = styled.input((props) => {
  const bgColor = props.isMobile ? white : appMainBackground;
  let style = {
    lineHeight: '18px',
    height: props.outsideHeight,
    width: '100%',
    border: '1px solid ' + unfocusedInputBorderColor,
    borderRadius: '4px',
    color: appTextColor,
    backgroundColor: bgColor,
    fontSize: '14px',
    fontFamily: 'inherit',
    paddingTop: '0px',
    paddingLeft: 12,
    paddingRight: 12,
    fontWeight: '200',
    '::placeholder': {
      fontWeight: '200'
    },
    ':focus': {
      outline: 'none',
      borderColor: focusedInputBorderColor
    },
    // this is a hack/clever fix which, when Chrome puts a yellow background to show that it
    //  has autofilled the input, adds a dropshadow which *looks* just like our background color
    //  because Chrome will not let us control the background color no matter what
    ':-webkit-autofill': {
      boxShadow: '0 0 0 ' + props.outsideHeight + 'px ' + bgColor + ' inset'
    }
  };
  if (props.displayError) {
    style.borderColor = warningRedColor;
    style[':focus'].borderColor = warningRedColor;
  }
  return style;
});

const EmailLauncherButton = styled(LaunchpadButton)((props) => {
  let style = {
    width: !props.isMobile ? '27%' : 'auto',
    height: '36px',
    paddingLeft: '20px',
    paddingRight: '20px',
    marginTop: '20px',
    float: !props.isMobile ? 'right' : 'left',
    marginRight: !props.isMobile ? '28px' : '10px'
  };
  if (props.isMobile) {
    style.borderRadius = '4px';
  }

  return style;
});

const EmailStatusDisp = styled.div((props) => {
  let style = {
    paddingTop: !props.isMobile ? '10px' : '20px',
    fontSize: '13px',
    display: 'block',
    color: appTextColor
  };
  if (!props.isMobile) {
    style.clear = 'both';
  }
  return style;
});

const CheckIcon = styled.div(() => {
  let style = {
    display: 'inline-block',
    backgroundRepeat: 'no-repeat',
    backgroundImage: 'url(' + checkIcon + ')',
    backgroundPosition: '0px 1px',
    backgroundSize: '12px 9px',
    width: '16px',
    height: '10px'
  };
  return style;
});

//*********************************************
export class EmailLauncher extends PureComponent {
  componentWillMount = () => {
    let providedEmail = this.props.userEmail();
    if (providedEmail && providedEmail.endsWith('@example.com')) {
      providedEmail = '';
    }
    this.setState({
      tempEmail: providedEmail
    });
  };

  handleEmailChange = (event) => {
    this.setState({ tempEmail: event.target.value });
    this.props.setMobileEmailStatus(EMAIL_STATUS.NOT_SENT);
  };

  handleEmailLauncherClick = () => {
    this.props.sendMobileEmail(this.state.tempEmail);
  };

  mapEnterToAction = (event) => {
    if (event.key === 'Enter' && !this.isSendDisabled()) {
      this.handleEmailLauncherClick();
    }
  };

  isSendDisabled = () => {
    return this.props.mobileEmailStatus !== EMAIL_STATUS.NOT_SENT || validateUserInfo('email', this.state.tempEmail);
  };

  isEmailInputDisabled = () => {
    return (
      this.props.mobileEmailStatus === EMAIL_STATUS.SEND_ATTEMPTED ||
      this.props.mobileEmailStatus === EMAIL_STATUS.SENT_SUCCESSFULLY
    );
  };

  /*
    render
   */
  render = () => {
    if (!this.props.lionLoaded) {
      return null;
    }

    const isMobile = isMobileDetected;

    let emailSendFailed =
      this.props.mobileEmailStatus === EMAIL_STATUS.INVALID ||
      this.props.mobileEmailStatus === EMAIL_STATUS.SENT_UNSUCCESSFULLY;

    const emailButtonTextKey = emailSendFailed ? 'email_resend_button' : 'email_send_button';

    let statusDisp = null;
    if (this.props.mobileEmailStatus === EMAIL_STATUS.SENT_SUCCESSFULLY) {
      statusDisp = (
        <EmailStatusDisp isMobile={isMobile}>
          <CheckIcon />
          <LionTranslate lionkey="email_text_sent" />
        </EmailStatusDisp>
      );
    } else if (emailSendFailed) {
      statusDisp = (
        <EmailStatusDisp isMobile={isMobile}>
          <LionTranslate lionkey="email_text_not_sent" />
        </EmailStatusDisp>
      );
    }

    return (
      <EmailLauncherBox isMobile={isMobile}>
        <EmailLauncherHeading>{this.props.lionTranslateString('email_header')}</EmailLauncherHeading>
        <LaunchContainer isMobile={isMobile}>
          <InputWithoutIcon
            inputType="text"
            data-qa="EmailLauncherEmailInput"
            placeholder={this.props.lionTranslateString('login_email_field')}
            value={this.state.tempEmail}
            onChange={this.handleEmailChange}
            onKeyPress={this.mapEnterToAction}
            disabled={this.isEmailInputDisabled()}
            outsideHeight={40}
            displayError={emailSendFailed || validateUserInfo('email', this.state.tempEmail).length > 0}
            isMobile={isMobile}
          />
        </LaunchContainer>
        <EmailLauncherButton
          data-qa="EmailLauncherButton"
          onClick={this.handleEmailLauncherClick}
          disabled={this.isSendDisabled()}
          isMobile={isMobile}
        >
          <LionTranslate lionkey={emailButtonTextKey} />
        </EmailLauncherButton>
        {statusDisp}
      </EmailLauncherBox>
    );
  };
}

EmailLauncher.propTypes = {};

const mapStateToProps = (state, ownprops) => ({
  isInterfaceRtl: appSelectors.isInterfaceRtl(state),
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  mobileEmailLauncherType: launchpadSelectors.mobileEmailLauncherType(state),
  mobileEmailStatus: launchpadSelectors.mobileEmailStatus(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  },
  userEmail: () => {
    const info = authSelectors.userInfo(state);
    if (info && info.email) {
      return info.email;
    }
    return '';
  }
});

const wrapActions = {
  clearOptionalTrigger: launchpadActions.clearOptionalTrigger,
  sendMobileEmail: launchpadActions.sendMobileEmail,
  setMobileEmailStatus: launchpadActions.setMobileEmailStatus
};
export const EmailLauncherContainer = connect(
  mapStateToProps,
  wrapActions
)(withWindowDimensions(EmailLauncher));



// WEBPACK FOOTER //
// ./src/modules/launchpad/components/EmailLauncher.jsx